// #region Imports

import type { IThemeElevation } from './Utils/IThemeElevation';
import type { IThemeLayout } from './Utils/IThemeLayout';
import type { ITheme } from './Utils/ITheme';
import type { ThemeScheme } from './Utils/ThemeScheme';
import type { ThemeTypography } from './Utils/ThemeTypography';
import type { ThemeMode } from './Utils/ThemeMode';
import type { ThemeSemantic } from './Utils/ThemeSemantic';

// #endregion

/**
 * @private
 */
export type ThemeName = string;

/**
 * @private
 */
export type SemanticColorName = string;

/**
 * @private
 */
export interface IThemeInit {
    name: string;
    scheme: Readonly<Record<ThemeMode, ThemeScheme>>;
    palette: Readonly<Record<ThemeMode, ThemeSemantic>>;
    fontFamily: string;
    typography: ThemeTypography;
    layout: IThemeLayout;
    elevation: Record<string, IThemeElevation>;
}

/**
 * Factory for creating fully dynamic themes.
 *
 * @private
 */
export class ThemeFactory {

    /**
     * Creates a new theme.
     *
     * @public
     * @static
     */
    public static create(init: IThemeInit): ITheme {
        return {
            name: init.name,
            scheme: init.scheme,
            palette: init.palette,
            fontFamily: init.fontFamily,
            typography: init.typography,
            layout: init.layout,
            elevation: init.elevation
        };
    }

    /**
     * Merges the given theme with the initial theme.
     *
     * @public
     * @static
     */
    public static merge(init: IThemeInit, theme: ITheme): ITheme {
        return {
            name: init.name,
            scheme: {
                ...theme.scheme,
                ...init.scheme
            },
            palette: {
                ...theme.palette,
                ...init.palette
            },
            fontFamily: init.fontFamily.length > 0 ? init.fontFamily : theme.fontFamily,
            typography: {
                ...theme.typography,
                ...init.typography
            },
            layout: {
                ...theme.layout,
                ...init.layout
            },
            elevation: {
                ...theme.elevation,
                ...init.elevation
            }
        };
    }

}
